import { Tag, Bell, Brain, LayoutDashboard, Smartphone, MapPin, Clock, Zap } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const PlatformPage = () => {
  const { t } = useLanguage();

  const engineTagFeatures = t('platform.engineTags.features') || [];
  const alertFeatures = t('platform.alerts.features') || [];
  const intelligenceFeatures = t('platform.intelligence.features') || [];
  const dashboardFeatures = t('platform.dashboard.features') || [];

  const sections = [
    {
      key: 'engineTags',
      icon: Tag,
      title: t('platform.engineTags.title'),
      description: t('platform.engineTags.description'),
      features: engineTagFeatures,
      image: '/engine_tag_photo.jpg',
      color: 'sky'
    },
    {
      key: 'alerts',
      icon: Bell,
      title: t('platform.alerts.title'),
      description: t('platform.alerts.description'),
      features: alertFeatures,
      image: '/screenshot_incident_log.png',
      color: 'amber'
    },
    {
      key: 'intelligence',
      icon: Brain,
      title: t('platform.intelligence.title'),
      description: t('platform.intelligence.description'),
      features: intelligenceFeatures,
      image: '/screenshot_analytics.png',
      color: 'emerald'
    },
    {
      key: 'dashboard',
      icon: LayoutDashboard,
      title: t('platform.dashboard.title'),
      description: t('platform.dashboard.description'),
      features: dashboardFeatures,
      image: '/screenshot_dashboard.png',
      color: 'violet'
    }
  ];

  const colorClasses = {
    sky: 'bg-[#0077FF]/10 text-[#0077FF]',
    amber: 'bg-amber-100 text-amber-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    violet: 'bg-violet-100 text-violet-600'
  };

  return (
    <div className="min-h-screen" data-testid="platform-page">
      {/* Hero */}
      <section className="gradient-hero pt-32 md:pt-40 pb-20 md:pb-24">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-caption text-[#0077FF] mb-4 block">Platform</span>
            <h1 className="text-h1 text-slate-900 mb-6" data-testid="platform-title">
              {t('platform.hero.title')}
            </h1>
            <p className="text-body-lg">
              {t('platform.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Platform Flow Overview */}
      <section className="section-padding bg-slate-900">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: Tag, label: 'Engine Tags', desc: 'Capture' },
              { icon: Bell, label: 'Real-Time Alerts', desc: 'Notify' },
              { icon: Brain, label: 'Intelligence', desc: 'Process' },
              { icon: LayoutDashboard, label: 'Dashboard', desc: 'Visualize' }
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-slate-800/50 rounded-xl border border-slate-700">
                <div className="w-14 h-14 mx-auto mb-4 bg-[#0077FF]/20 rounded-xl flex items-center justify-center">
                  <item.icon className="w-7 h-7 text-[#4DB8FF]" />
                </div>
                <p className="text-white font-semibold mb-1">{item.label}</p>
                <p className="text-[#4DB8FF] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      {sections.map((section, index) => (
        <section
          key={section.key}
          className={`section-padding ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}
          data-testid={`platform-section-${section.key}`}
        >
          <div className="container-max">
            <div className={`grid md:grid-cols-2 gap-12 lg:gap-20 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${colorClasses[section.color]} text-sm font-medium mb-6`}>
                  <section.icon className="w-4 h-4" />
                  <span>Feature</span>
                </div>
                <h2 className="text-h2 text-slate-900 mb-4">
                  {section.title}
                </h2>
                <p className="text-body-lg mb-8">
                  {section.description}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {section.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 rounded-full ${section.color === 'sky' ? 'bg-[#0077FF]' : section.color === 'amber' ? 'bg-amber-500' : section.color === 'emerald' ? 'bg-emerald-500' : 'bg-violet-500'}`} />
                      <span className="text-slate-700 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                <img
                  src={section.image}
                  alt={section.title}
                  className={`rounded-2xl shadow-elevated w-full ${section.key === 'engineTags' ? 'object-contain bg-slate-100 aspect-auto max-h-[400px]' : 'object-cover aspect-[4/3]'}`}
                />
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default PlatformPage;
